import { toast } from 'sonner';
import { workorderActions, workorderStatuses } from './api';

// Doing one thing to several jobs at once. Written once because the office does
// it from two places — a customer's job history and the schedule's list — and
// because what gets said afterwards has to be the server's count, not the
// number of rows that happened to be ticked. A job already invoiced is skipped
// rather than billed twice, and the office needs to see the difference between
// "billed forty" and "billed none, they were all billed already".

/**
 * Raise an invoice for each job. Returns true when anything was written, so
 * the caller knows whether to reload.
 */
export async function billJobs(ids, { confirm = true } = {}) {
  if (!ids || !ids.length) return false;
  if (confirm && !window.confirm(
    `Create an invoice for ${ids.length} job(s)? `
    + 'Jobs that already have an invoice are skipped.')) return false;
  try {
    const r = await workorderActions.bulkInvoice(ids);
    const left = [
      r.alreadyInvoiced ? `${r.alreadyInvoiced} already invoiced` : '',
      r.withoutAmount ? `${r.withoutAmount} with no amount on the job` : '',
    ].filter(Boolean).join(', ');
    if (r.created) {
      toast.success(`${r.created} invoice(s) created${left ? ` · skipped ${left}` : ''}`);
    } else {
      // Not an error, but not a success either: nothing was written, and saying
      // "done" would send the office looking for invoices that are not there.
      toast.error(left ? `Nothing to bill — ${left}` : 'No invoices were created');
    }
    return r.created > 0;
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not bill those jobs');
    return false;
  }
}

/** Move several jobs to one status. Returns true when anything changed. */
export async function setJobStatus(ids, status) {
  if (!ids || !ids.length) return false;
  try {
    const r = await workorderStatuses.setMany(ids, status);
    toast.success(`${r.updated} job(s) set to ${status}`);
    return r.updated > 0;
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not update those jobs');
    return false;
  }
}
